// File: src/pages/PurchaseOrderViewList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Table,
} from "reactstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import swal from "sweetalert";

import { FaPlus, FaFilter, FaPencilAlt, FaPaperPlane } from "react-icons/fa";
import { ImDownload } from "react-icons/im";

// ---- shared layer ----
import { _Post, _GetById } from "../import/ApiEndPoint/ApiCalling";
import { CheckPermission } from "../import/CheckPermission";
import { exportDataToPDF } from "../import/Downloader";
import {
  View_Invoice_Data_by_id,
  Send_Purchase_Order_Mail,
} from "../import/ApiEndPoint/Api";
import { PurchaseOrderList } from "../import/ApiEndPoint/ApiCalling";
import LastestInvoice from "./LastestInvoice";
import { pdf } from "@react-pdf/renderer";
import LinkCell from "../components/LinkCell";

// ----------------- inline CSS (page-scoped) -----------------
const injectOnce = () => {
  if (document.getElementById("po-inline-css")) return;
  const style = document.createElement("style");
  style.id = "po-inline-css";
  style.innerHTML = `
    .po-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:900; padding:6px 10px; border-radius:999px;
    }

    .po-card{ border:1px solid #e5e7eb; border-radius:14px; background:#f4f7f6; padding:10px 12px; }
    .po-title{ font-weight:900; text-transform:uppercase; font-size:18px; letter-spacing:.02em; display:flex; gap:10px; align-items:center; flex-wrap:wrap; }
    .po-actions{ display:flex; gap:8px; align-items:center; flex-wrap:wrap; justify-content:flex-end; }
    .po-search{ min-width:260px; }
    .po-btn{
      height:34px; display:flex; align-items:center; justify-content:center; gap:8px;
      border-radius:12px; font-weight:800; letter-spacing:.02em; text-transform:uppercase;
    }
    .po-btn-outline{
      height:34px; display:flex; align-items:center; justify-content:center; gap:8px;
      border-radius:12px; font-weight:800; letter-spacing:.02em;
      background:#fff;
    }

    .po-filterbar{
      margin-top:10px;
      background:#fff;
      border:1px dashed #cbd5e1;
      border-radius:14px;
      padding:10px 12px;
    }
    .po-filter-row{
      display:flex;
      gap:10px;
      flex-wrap:wrap;
      align-items:flex-end;
    }
    .po-fitem{ flex:1 1 180px; min-width:180px; }
    .po-fitem label{ font-size:12px; font-weight:900; color:#0f172a; letter-spacing:.02em; margin-bottom:4px; }
    .po-fitem .form-control{ border-radius:12px; }
    .po-chipline{ margin-top:10px; display:flex; flex-wrap:wrap; gap:8px; }
    .po-chip{
      display:inline-flex; align-items:center; gap:8px;
      padding:6px 10px; border-radius:999px;
      background:#f1f5f9; border:1px solid #e5e7eb; color:#0f172a;
      font-weight:900; font-size:12px; letter-spacing:.02em;
    }

    .po-table-wrap{
      width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff;
    }
    .po-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:900px; }
    .po-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:900; text-transform:uppercase; font-size:12px; letter-spacing:.04em;
      border-bottom:1px solid #e5e7eb; padding:8px 10px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .po-table tbody td{
      padding:8px 10px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .po-table tbody tr:hover{ background:#f9fbff; }

    .action-chips{ display:flex; justify-content:center; align-items:center; gap:8px }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; color:#fff; }
    .chip-edit{ background:#d46f10; }
    .chip-send{ background:#0b6bcb; }
    .chip-send.disabled{ opacity:.5; pointer-events:none; }

    .column-box{ border:1px solid #e5e7eb; border-radius:10px; max-height:280px; overflow:auto; background:#fff; padding:8px; }
    .column-chip{ padding:8px 10px; border-radius:8px; border:1px solid #e5e7eb; margin-bottom:6px; background:#fff; cursor:pointer; }
    .column-chip.active{ background:#1877f2; color:#fff; }
  `;
  document.head.appendChild(style);
};

// ----------------- helpers -----------------
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const safeFixed2 = (n) => {
  const x = Number(n);
  return Number.isFinite(x) ? x.toFixed(2) : "-";
};
const fmtDT = (d) => {
  const t = d ? new Date(d) : null;
  if (!t || isNaN(+t)) return "-";
  const dd = String(t.getDate()).padStart(2, "0");
  const mm = String(t.getMonth() + 1).padStart(2, "0");
  const yyyy = t.getFullYear();
  const hh = String(t.getHours()).padStart(2, "0");
  const mi = String(t.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${mi}`;
};
const getRowDate = (r) => {
  const d = r?.createdAt || r?.date || r?.updatedAt || r?.created_at;
  const t = d ? new Date(d) : null;
  return t && !isNaN(+t) ? t : null;
};
const extractText = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean")
    return String(v);
  if (Array.isArray(v)) return v.map(extractText).join(" ").trim();
  if (React.isValidElement(v)) return extractText(v.props?.children);
  return "";
};

// ----------------- component -----------------
export default function PurchaseOrderViewList() {
  const navigate = useNavigate();
  useEffect(() => injectOnce(), []);

  // state
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
  });

  const [rows, setRows] = useState([]); // store ALL orders (we filter via UI)
  const [search, setSearch] = useState("");

  // redesigned filters
  const [showFilters, setShowFilters] = useState(false);
  const [fStatus, setFStatus] = useState("pending"); // default SAME as your earlier page logic
  const [fSupplier, setFSupplier] = useState("");
  const [fFrom, setFFrom] = useState(""); // yyyy-mm-dd
  const [fTo, setFTo] = useState(""); // yyyy-mm-dd
  const [fMinGrand, setFMinGrand] = useState("");
  const [fMaxGrand, setFMaxGrand] = useState("");

  // column chooser
  const addBufferRef = useRef([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // view modal (kept as-is)
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  // ---- columns ----
  const baseColumns = useMemo(
    () => [
      {
        key: "status",
        header: "STATUS",
        get: (r) => r?.status || "-",
      },
      {
        key: "party",
        header: "SUPPLIER",
        get: (r) => r?.partyId?.CompanyName || "-",
        render: (r) => {
          return (
            <LinkCell
              onClick={() => {
                navigate(`/app/purchase-orders/edit/${r?._id}`);
              }}
            >
              {r?.partyId?.CompanyName || "-"}
            </LinkCell>
          );
        },
      },
      {
        key: "invoice",
        header: "PO / INVOICE NO.",
        get: (r) => {
          const company = JSON.parse(
            localStorage.getItem("Companydetail") || "{}",
          );
          const prefix = company?.Prefix || "";
          const suffix = company?.Suffix || "";
          const inv = r?.poNumber ?? r?.invoiceId ?? "";
          return inv ? `${prefix}${inv}${suffix}` : "-";
        },
        render: (r) => {
          const v = r?.invoiceId; // only this triggers PDF download (as per your original)
          const label = (() => {
            const company = JSON.parse(
              localStorage.getItem("Companydetail") || "{}",
            );
            const prefix = company?.Prefix || "";
            const suffix = company?.Suffix || "";
            const inv = r?.poNumber ?? r?.invoiceId ?? "";
            return inv ? `${prefix}${inv}${suffix}` : "-";
          })();
          if (!v) return label;
          return (
            <span
              title="Download Invoice PDF"
              style={{
                color: "blue",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => downloadInvoice(r)}
            >
              {label}
            </span>
          );
        },
      },
      {
        key: "tax",
        header: "TAX",
        get: (r) => {
          const tax =
            Number(r?.igstTotal) > 0
              ? Number(r?.igstTotal)
              : Number(r?.cgstTotal || 0) + Number(r?.sgstTotal || 0);
          return safeFixed2(tax);
        },
      },
      { key: "amount", header: "AMOUNT", get: (r) => safeFixed2(r?.amount) },
      {
        key: "charges",
        header: "CHARGES",
        get: (r) => safeFixed2(r?.coolieAndCartage),
      },
      {
        key: "roundOff",
        header: "ROUND OFF",
        get: (r) => safeFixed2(r?.roundOff),
      },
      {
        key: "grand",
        header: "GRAND TOTAL",
        get: (r) => safeFixed2(r?.grandTotal),
      },
      {
        key: "sentAt",
        header: "SENT AT",
        get: (r) => fmtDT(r?.emailSentAt || r?.sentAt),
      },
    ],
    [navigate],
  );

  const actionsColumn = useMemo(
    () => ({
      key: "_actions_",
      header: "ACTIONS",
      get: () => "",
      render: (r) => {
        const isConfirmed =
          String(r?.status || "").toLowerCase() === "confirmed";
        return (
          <div className="action-chips">
            {permissions?.Edit && (
              <span
                className="chip chip-edit"
                title="Edit"
                onClick={() => navigate(`/app/purchase-orders/edit/${r?._id}`)}
              >
                <FaPencilAlt size={14} />
              </span>
            )}

            <span
              className={`chip chip-send ${isConfirmed ? "disabled" : ""}`}
              title={
                isConfirmed
                  ? "Already sent/confirmed"
                  : "Send order via email & mark confirmed"
              }
              onClick={() => {
                if (!isConfirmed) handleSendByMail(r);
              }}
            >
              <FaPaperPlane size={14} />
            </span>
          </div>
        );
      },
    }),
    [permissions, navigate],
  );

  const colMap = useMemo(() => {
    const m = new Map();
    baseColumns.forEach((c) => m.set(c.key, c));
    m.set(actionsColumn.key, actionsColumn);
    return m;
  }, [baseColumns, actionsColumn]);

  const [selectedKeys, setSelectedKeys] = useState([]);
  useEffect(() => {
    const raw = localStorage.getItem("PurchaseOrderListCols");
    let next = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        next = parsed.filter((k) => colMap.has(k));
      }
    } catch {}
    if (!next.length) next = baseColumns.map((c) => c.key);
    setSelectedKeys(next);
  }, [baseColumns, colMap]);

  // fetch list
  const reload = async () => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    const financialYearSelected = localStorage.getItem("financialYearSelected");
    const selectedDatabase = `${user?.database}-${financialYearSelected}`;
    const inside = CheckPermission("Purchase Order");
    if (inside && typeof inside === "object") {
      setPermissions((p) => ({ ...p, ...inside }));
    }

    const res = await PurchaseOrderList(user?._id, selectedDatabase);
    const list = Array.isArray(res?.orderHistory) ? res.orderHistory : [];

    // compute tax col like original
    list.forEach((el) => {
      el.Tax =
        Number(el?.igstTotal) > 0
          ? Number(el?.igstTotal)
          : Number(el?.cgstTotal || 0) + Number(el?.sgstTotal || 0);
    });

    // keep original “latest first” feel
    setRows(list.slice().reverse());
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        await reload();
      } catch (e) {
        console.error("[PurchaseOrderViewList] fetch error:", e);
        setErrorMsg(
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load purchase orders. Check console for details.",
        );
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // visible cols + search
  const visibleCols = useMemo(() => {
    const cols = selectedKeys.map((k) => colMap.get(k)).filter(Boolean);
    return [...cols, actionsColumn];
  }, [selectedKeys, colMap, actionsColumn]);

  // --- redesigned filters pipeline (filters first, then search) ---
  const filteredByFilters = useMemo(() => {
    const st = String(fStatus || "")
      .trim()
      .toLowerCase(); // all/pending/confirmed/...
    const supTerm = String(fSupplier || "")
      .trim()
      .toLowerCase();

    // date range (inclusive)
    const from = fFrom ? new Date(`${fFrom}T00:00:00`) : null;
    const to = fTo ? new Date(`${fTo}T23:59:59`) : null;

    const minG = fMinGrand !== "" ? Number(fMinGrand) : null;
    const maxG = fMaxGrand !== "" ? Number(fMaxGrand) : null;

    return (rows || []).filter((r) => {
      if (st && st !== "all") {
        if (
          String(r?.status || "")
            .trim()
            .toLowerCase() !== st
        )
          return false;
      }

      if (supTerm) {
        const nm = String(r?.partyId?.CompanyName || "").toLowerCase();
        if (!nm.includes(supTerm)) return false;
      }

      const dt = getRowDate(r);
      if (from && dt && dt < from) return false;
      if (to && dt && dt > to) return false;

      const g = Number(r?.grandTotal);
      if (minG !== null && Number.isFinite(minG)) {
        if (!(Number.isFinite(g) ? g : 0) >= minG) return false;
      }
      if (maxG !== null && Number.isFinite(maxG)) {
        if (!(Number.isFinite(g) ? g : 0) <= maxG) return false;
      }

      return true;
    });
  }, [rows, fStatus, fSupplier, fFrom, fTo, fMinGrand, fMaxGrand]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return filteredByFilters;

    const cols = visibleCols.filter((c) => c.key !== "_actions_");
    return (filteredByFilters || []).filter((r) =>
      cols.some((c) => toStr(c.get(r)).toLowerCase().includes(term)),
    );
  }, [filteredByFilters, search, visibleCols]);

  // active chips
  const chips = useMemo(() => {
    const out = [];
    if (String(fStatus || "").toLowerCase() !== "pending") {
      out.push({ k: "Status", v: fStatus || "pending" });
    }
    if (fSupplier.trim()) out.push({ k: "Supplier", v: fSupplier.trim() });
    if (fFrom) out.push({ k: "From", v: fFrom });
    if (fTo) out.push({ k: "To", v: fTo });
    if (fMinGrand !== "") out.push({ k: "Min Total", v: fMinGrand });
    if (fMaxGrand !== "") out.push({ k: "Max Total", v: fMaxGrand });
    return out;
  }, [fStatus, fSupplier, fFrom, fTo, fMinGrand, fMaxGrand]);

  const clearFilters = () => {
    setFStatus("pending");
    setFSupplier("");
    setFFrom("");
    setFTo("");
    setFMinGrand("");
    setFMaxGrand("");
  };

  // send by mail -> confirm -> remove from pending list (kept same behavior)
  const handleSendByMail = async (row) => {
    const ok = await swal({
      title: "Send this order by email?",
      text: "It will be emailed to the supplier and marked as CONFIRMED. This item will be removed from the pending list.",
      icon: "warning",
      buttons: ["Cancel", "Send"],
      dangerMode: false,
    });
    if (!ok) return;

    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const res = await _Post(`${Send_Purchase_Order_Mail}/${row?._id}`, {
        database: user?.database,
      });

      const updated = res?.order || res?.orderDetail || {};
      const sentWhen = updated?.emailSentAt || new Date().toISOString();

      swal(
        "Sent!",
        `Order mailed & confirmed.\nSent at: ${fmtDT(sentWhen)}`,
        "success",
      );

      // remove from list only when you are in pending filter (old behavior)
      setRows((prev) => prev.filter((x) => x?._id !== row?._id));
    } catch (e) {
      console.error("Send PO mail error:", e);
      swal(
        "Failed",
        e?.response?.data?.message || e?.message || "Unable to send email.",
        "error",
      );
    }
  };

  // ---------- EXPORTS (Buttons) ----------
  const exportableCols = useMemo(
    () => visibleCols.filter((c) => c.key !== "_actions_"),
    [visibleCols],
  );

  const cellText = (c, r) => {
    const v = typeof c.render === "function" ? c.render(r) : c.get(r);
    const t = extractText(v);
    return toStr(t || c.get(r));
  };

  const handleExportPDF = async () => {
    const cols = exportableCols;
    const header = cols.map((c) => c.header).join(",");
    const lines = (filteredRows || []).map((r) =>
      cols.map((c) => `"${cellText(c, r).replace(/"/g, '""')}"`).join(","),
    );
    const csv = [header, ...lines].join("\r\n");
    await exportDataToPDF(csv, "PurchaseOrderList");
  };

  const handleExportXLSX = async (fileName = "PurchaseOrderList") => {
    const cols = exportableCols;
    const data = (filteredRows || []).map((r) => {
      const obj = {};
      cols.forEach((c) => (obj[c.header] = cellText(c, r)));
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`,
    );
  };

  // column chooser modal
  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);

  const handleCheckbox = (checked, key) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(key)) buf.push(key);
    } else {
      const i = buf.indexOf(key);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };
  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current]),
    );
    setSelectedKeys(next);
  };
  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };
  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };
  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };
  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem("PurchaseOrderListCols", JSON.stringify(selectedKeys));
    closeModal();
  };

  // invoice download
  const downloadInvoice = async (row) => {
    try {
      if (!row?.invoiceId) {
        swal("Invoice ID not found");
        return;
      }
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const res = await _GetById(
        View_Invoice_Data_by_id,
        user?.database,
        row?.invoiceId,
      );
      const printData = res?.printData;
      if (!printData) {
        swal("Invoice data not found");
        return;
      }
      const details = Array.isArray(printData?.orderItems)
        ? printData.orderItems
        : [];
      const blob = await pdf(
        <LastestInvoice
          invoiceData={printData}
          CollectedData={{}}
          BilData={{
            CompanyDetails: JSON.parse(
              localStorage.getItem("Companydetail") || "{}",
            ),
          }}
          tableList={details}
          AllCharges={{}}
          fileName="invoice.pdf"
        />,
      ).toBlob();

      const partyName = (printData?.partyId?.CompanyName || "Invoice").replace(
        /[^a-zA-Z0-9_-]/g,
        "_",
      );
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = `${partyName}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Invoice download error:", e);
      swal("Something went wrong while downloading.");
    }
  };

  // loading
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* ===== Toolbar ===== */}
      <Row className="po-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card className="po-card">
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div className="po-title">
                Purchase Order List{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
                <span
                  style={{ fontWeight: 800, color: "#64748b", fontSize: 12 }}
                >
                  (Total: {rows.length})
                </span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              <div className="po-actions">
                <div className="po-search">
                  <Input
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ background: "#fff" }}
                  />
                </div>

                <Button
                  className="po-btn-outline"
                  outline
                  color="primary"
                  onClick={() => setShowFilters((s) => !s)}
                  title="Show/Hide Filters"
                >
                  <FaFilter size={14} /> Filters
                </Button>

                <Button
                  className="po-btn-outline"
                  outline
                  color="primary"
                  onClick={openModal}
                  title="Choose Columns"
                >
                  <FaFilter size={14} /> Columns
                </Button>

                {permissions?.Create && (
                  <Button
                    className="po-btn"
                    color="primary"
                    onClick={() => navigate("/app/purchase-orders/create")}
                    title="Create Purchase Order"
                  >
                    <FaPlus size={12} /> Add PO
                  </Button>
                )}

                {permissions?.Download && (
                  <>
                    <Button
                      className="po-btn"
                      color="primary"
                      onClick={() => handleExportXLSX("PurchaseOrderList")}
                      title="Download Excel"
                    >
                      <ImDownload /> Excel
                    </Button>
                    {/* <Button
                      className="po-btn-outline"
                      outline
                      color="primary"
                      onClick={handleExportPDF}
                      title="Download PDF"
                    >
                      <ImDownload /> PDF
                    </Button> */}
                  </>
                )}
              </div>
            </div>

            {/* ===== Redesigned Filters Panel ===== */}
            {showFilters && (
              <div className="po-filterbar">
                <div className="po-filter-row">
                  <div className="po-fitem">
                    <label>Status</label>
                    <Input
                      type="select"
                      value={fStatus}
                      onChange={(e) => setFStatus(e.target.value)}
                    >
                      <option value="pending">Pending (Default)</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="all">All</option>
                    </Input>
                  </div>

                  <div className="po-fitem">
                    <label>Supplier (contains)</label>
                    <Input
                      placeholder="Type supplier name..."
                      value={fSupplier}
                      onChange={(e) => setFSupplier(e.target.value)}
                    />
                  </div>

                  <div className="po-fitem">
                    <label>Date From</label>
                    <Input
                      type="date"
                      value={fFrom}
                      onChange={(e) => setFFrom(e.target.value)}
                    />
                  </div>

                  <div className="po-fitem">
                    <label>Date To</label>
                    <Input
                      type="date"
                      value={fTo}
                      onChange={(e) => setFTo(e.target.value)}
                    />
                  </div>

                  <div className="po-fitem">
                    <label>Min Grand Total</label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={fMinGrand}
                      onChange={(e) => setFMinGrand(e.target.value)}
                    />
                  </div>

                  <div className="po-fitem">
                    <label>Max Grand Total</label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={fMaxGrand}
                      onChange={(e) => setFMaxGrand(e.target.value)}
                    />
                  </div>

                  <div className="po-fitem" style={{ minWidth: 200 }}>
                    <label>Actions</label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Button
                        className="po-btn-outline"
                        outline
                        color="secondary"
                        onClick={clearFilters}
                        title="Reset Filters"
                      >
                        Clear
                      </Button>
                      <Button
                        className="po-btn-outline"
                        outline
                        color="primary"
                        onClick={() => reload()}
                        title="Refresh list from server"
                      >
                        Refresh
                      </Button>
                    </div>
                  </div>
                </div>

                {!!chips.length && (
                  <div className="po-chipline">
                    {chips.map((c, i) => (
                      <span className="po-chip" key={`${c.k}-${i}`}>
                        {c.k}: {c.v}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ===== Table ===== */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No purchase orders found.
              </div>
            ) : (
              <div className="po-table-wrap">
                <table className="po-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r) => (
                      <tr key={r?._id}>
                        {visibleCols.map((c) => (
                          <td key={c.key}>
                            {typeof c.render === "function"
                              ? c.render(r)
                              : toStr(c.get(r))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ===== Column Chooser Modal ===== */}
      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12">
              <h5>Available Columns</h5>
              <div className="column-box">
                {baseColumns.map((col) => (
                  <div key={col.key} className="column-chip">
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        margin: 0,
                      }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                        style={{ marginTop: 2 }}
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div className="column-box" style={{ flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        className={`column-chip ${i === selectedIndex ? "active" : ""}`}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      {/* ===== View Modal (kept) ===== */}
      <Modal
        isOpen={viewOpen}
        toggle={() => setViewOpen(false)}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={() => setViewOpen(false)}>
          Purchase View
        </ModalHeader>
        <ModalBody>
          {!viewRow ? (
            <div style={{ padding: 16, textAlign: "center" }}>No data.</div>
          ) : (
            <>
              <Row>
                <Col>
                  <b>Company:</b> {viewRow?.partyId?.CompanyName || "-"}
                </Col>
                <Col>
                  <b>Amount:</b> {safeFixed2(viewRow?.amount)}
                </Col>
                {Number(viewRow?.igstTaxType) === 1 ? (
                  <Col>
                    <b>IGST:</b> {safeFixed2(viewRow?.igstTotal)}
                  </Col>
                ) : (
                  <>
                    <Col>
                      <b>SGST:</b> {safeFixed2(viewRow?.sgstTotal)}
                    </Col>
                    <Col>
                      <b>CGST:</b> {safeFixed2(viewRow?.cgstTotal)}
                    </Col>
                  </>
                )}
                <Col>
                  <b>Grand Total:</b> {safeFixed2(viewRow?.grandTotal)}
                </Col>
              </Row>

              <Row className="mt-2">
                <Col>
                  <div style={{ fontWeight: 700, textAlign: "center" }}>
                    Items
                  </div>
                </Col>
              </Row>

              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  overflow: "hidden",
                  marginTop: 6,
                }}
              >
                <Table responsive hover style={{ margin: 0 }}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Product</th>
                      <th>HSN</th>
                      <th>Price</th>
                      <th>Qty</th>
                      <th>Unit</th>
                      <th>Taxable</th>
                      {Number(viewRow?.igstTaxType) === 1 ? (
                        <th>IGST</th>
                      ) : (
                        <>
                          <th>SGST</th>
                          <th>CGST</th>
                        </>
                      )}
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewRow?.orderItems || []).map((it, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{it?.productId?.Product_Title}</td>
                        <td>{it?.productId?.HSN_Code}</td>
                        <td>{toStr(it?.basicPrice)}</td>
                        <td>{toStr(it?.qty)}</td>
                        <td>{toStr(it?.productId?.primaryUnit)}</td>
                        <td>{toStr(it?.taxableAmount)}</td>
                        {Number(viewRow?.igstTaxType) === 1 ? (
                          <td>{toStr(it?.igstRate)}</td>
                        ) : (
                          <>
                            <td>{toStr(it?.sgstRate)}</td>
                            <td>{toStr(it?.cgstRate)}</td>
                          </>
                        )}
                        <td>{toStr(it?.grandTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}
